import { Table, StatusPill } from '../../../design-system';
import { useReport, ReportCard, StatRow, Stat, BarList, DivergingBars, AreaTrend, fmtNum, fmtHours } from './reportsKit';

export default function WorkloadReports({ range }) {
  const workload = useReport('staff-workload');
  const bva = useReport('budget-vs-actual', range);
  const effort = useReport('effort', range);

  return (
    <div className="rep-stack">
      {/* B1 — Staff Workload */}
      <ReportCard icon="👥" title="Staff Workload" subtitle="Open tasks and budgeted hours per person" loading={workload.loading} error={workload.error}>
        <div className="rep-grid">
          <div>
            <p className="rep-note">Open tasks per person</p>
            <BarList items={(workload.data?.rows || []).map((r) => ({ label: r.name, value: r.openTasks }))} color="var(--ds-accent-600)" />
          </div>
          <div>
            <p className="rep-note">Budgeted hours per person</p>
            <BarList items={(workload.data?.rows || []).map((r) => ({ label: r.name, value: r.budgetedHours }))} valueFmt={fmtHours} color="var(--ds-info-600)" />
          </div>
        </div>
        <p className="rep-note">Detail</p>
        <Table
          keyField="userId"
          columns={[
            { key: 'name', header: 'Person', render: (r) => <strong>{r.name}</strong> },
            { key: 'openTasks', header: 'Open' },
            { key: 'overdue', header: 'Overdue', render: (r) => (r.overdue > 0 ? <StatusPill variant="danger" label={fmtNum(r.overdue)} /> : '0') },
            { key: 'budgetedHours', header: 'Budgeted', render: (r) => fmtHours(r.budgetedHours) },
            { key: 'roles', header: 'P / R / A', render: (r) => `${r.asPreparer} / ${r.asReviewer} / ${r.asAssistant}` },
          ]}
          data={workload.data?.rows || []}
        />
      </ReportCard>

      {/* B2 — Budget vs Actual */}
      <ReportCard icon="⏱️" title="Budget vs Actual Hours" subtitle="Who ran over (red) or under (green) budget, this period" loading={bva.loading} error={bva.error}>
        <p className="rep-note">Variance by person</p>
        <DivergingBars items={(bva.data?.perPerson || []).map((p) => ({ label: p.name, value: p.variance }))} valueFmt={fmtHours} emptyText="No hours logged in range" />
        <p className="rep-note">Largest task variances</p>
        <Table
          keyField="id"
          columns={[
            { key: 'title', header: 'Task', render: (r) => <span title={r.title}>{r.taskNumber ? `TASK-${String(r.taskNumber).padStart(4, '0')} ` : ''}{(r.title || '').slice(0, 30)}</span> },
            { key: 'budgeted', header: 'Budget', render: (r) => fmtHours(r.budgeted) },
            { key: 'actual', header: 'Actual', render: (r) => fmtHours(r.actual) },
            { key: 'variance', header: 'Variance', render: (r) => <span className={`rep-tone-${r.variance > 0 ? 'red' : 'green'}`}>{r.variance > 0 ? '+' : ''}{fmtHours(r.variance)}</span> },
          ]}
          data={(bva.data?.perTask || []).slice(0, 12)}
        />
      </ReportCard>

      {/* B3 — Effort / Time Logged */}
      <ReportCard icon="📊" title="Effort / Time Logged" subtitle="Hours logged by month, person and client" loading={effort.loading} error={effort.error}>
        <StatRow>
          <Stat label="Total hours logged" value={fmtHours(effort.data?.totalHours)} tone="accent" />
          <Stat label="People logging" value={fmtNum(effort.data?.byPerson?.length)} tone="blue" />
          <Stat label="Clients served" value={fmtNum(effort.data?.byClient?.length)} tone="slate" />
        </StatRow>
        <p className="rep-note">Hours per month</p>
        <AreaTrend data={(effort.data?.byMonth || []).map((m) => ({ key: m.key, value: m.hours }))} valueFmt={fmtHours} color="var(--ds-accent-600)" />
        <div className="rep-grid">
          <div>
            <p className="rep-note">By person</p>
            <BarList items={(effort.data?.byPerson || []).map((p) => ({ label: p.name, value: p.hours }))} valueFmt={fmtHours} color="var(--ds-info-600)" />
          </div>
          <div>
            <p className="rep-note">By client</p>
            <BarList items={(effort.data?.byClient || []).slice(0, 8).map((c) => ({ label: c.name, value: c.hours }))} valueFmt={fmtHours} color="var(--ds-success-600)" />
          </div>
        </div>
      </ReportCard>
    </div>
  );
}
